import { useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBreadcrumbLabel } from '@/components/breadcrumb';
import { useAccess } from '@/lib/access/use-access';
import { useDisruption } from '@/lib/disruptions/use-disruptions';
import type { DisruptionCaseRow, DisruptionDetail } from '@/lib/disruptions/types';
import { formatDateTime } from './labels';
import { formatFlightDay } from '@/lib/disruptions/airport-tz';
import { DisruptionHeader } from './disruption-header';
import { joinDisruption, leaveDisruption } from '@/lib/ws/socket';
import { RosterTab } from './roster-tab';
import { EventAllocationTab } from './event-allocation-tab';
import { CareDeskTab } from './care-desk/care-desk-tab';
import { TransportDeskTab } from './transport-desk/transport-desk-tab';
import { CostTab } from './cost-tab';
import { ReconciliationTab } from './reconciliation-tab';
import { OperatorActivityTab } from './operator-activity-tab';
import { DisruptionResolutionCockpit } from './disruption-resolution-cockpit';
import { DisruptionCommandCenterV2 } from './alternative/disruption-command-center-v2';

// Disruption CONTROL CENTER (S7 / NEX-282) per docs/domains/disruption.tds.md §5
// + the disruption-control-center brief §1/§7 + docs/ui/frontend-conventions.md
// §18 (no-page-scroll app-shell, friendly labels over URNs, white-label tokens).
//
// The detail page is now the control center: an Overview tab (the original
// header + scope + rollups + child cases) plus Passengers (flat roster, infinite
// scroll + filters), Progress (live engine poll), and Cost (per-flight drill) —
// so the operator manages the whole event without entering a single case. Tab
// state lives in the URL hash (#overview default) so deep links survive copy-paste.

const TAB_VALUES = ['overview', 'v2', 'passengers', 'allocation', 'transport', 'care', 'cost', 'reconciliation', 'activity'] as const;
type TabValue = (typeof TAB_VALUES)[number];

// The hash may carry tab-scoped params after `?` (the Tower grammar) — e.g. the
// Care deep link `#care?pnr=<subCaseUrn>` from the roster's meal-voucher card.
function activeTabFromHash(hash: string): TabValue {
  // Tolerate hash params (`#care?pnr=…` / `#transport?hotel=…` — the deep-link
  // grammar): the tab is the segment before `?`; each tab parses its own params.
  const stripped = hash.replace(/^#/, '').split('?')[0] ?? '';
  return (TAB_VALUES as readonly string[]).includes(stripped) ? (stripped as TabValue) : 'overview';
}

function hashParam(hash: string, key: string): string | undefined {
  const q = hash.indexOf('?');
  if (q === -1) return undefined;
  return new URLSearchParams(hash.slice(q + 1)).get(key) ?? undefined;
}

export function DisruptionDetailRoute() {
  const { t } = useTranslation('disruptions');
  const { disruptionUrn: raw } = useParams<{ disruptionUrn: string }>();
  const urn = raw ? decodeURIComponent(raw) : undefined;
  const { data, isLoading, isError, refetch } = useDisruption(urn);
  const { hash } = useLocation();
  const navigate = useNavigate();
  // The per-passenger reconciliation is `reconciliation.view` (supervisor + finance,
  // `auth:PermissionCatalog@v1`); without it the tab is not offered and its hash opens Overview.
  const access = useAccess();
  const canReconcile = access.can('reconciliation.view');
  // O3: the airline opens this disruption's billing statement from here (`billing.view`).
  const canBill = access.can('billing.view');
  const { t: tb } = useTranslation('billing');
  const fromHash = activeTabFromHash(hash);
  const active = fromHash === 'reconciliation' && !canReconcile ? 'overview' : fromHash;

  // Name this disruption's breadcrumb crumb with its friendly title so the header
  // reads `Disruptions › S21 verify`, not `Disruptions › urn:disruption:…`
  // (frontend-conventions §18.3 — URNs are secondary). Pure cache write from the
  // detail we already loaded: no extra read.
  useBreadcrumbLabel(urn, data?.title);

  // Subscribe to the per-disruption Socket.IO room for the lifetime of the
  // detail page (NEX-293): the BFF fans out live progress + bulk run-state to
  // this room, so the Progress tab and the bulk bar update over the WS (no
  // polling). The socket is a no-op when not connected, so this is safe offline.
  useEffect(() => {
    if (!urn) return;
    joinDisruption(urn);
    return () => leaveDisruption(urn);
  }, [urn]);

  const onTabChange = (next: string): void => {
    navigate({ hash: `#${next}` }, { replace: true });
  };

  // ERROR (§18.1 frame preserved): the app-shell already owns the page's single
  // <main>, so this must never nest another one. The failure is actionable —
  // Retry re-runs the detail read, and the escape hatch back to the list is
  // explicit — and it lives inside a bounded scroll region like every other state.
  if (isError) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="disruption-detail-error">
        <div className="density-page min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <EmptyState
            icon={<AlertTriangle className="h-6 w-6" aria-hidden />}
            title={t('detail.error.title')}
            description={t('detail.error.description')}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => void refetch()}>{t('detail.error.retry')}</Button>
                <Button variant="outline" asChild>
                  <Link to="/disruptions">{t('detail.error.back')}</Link>
                </Button>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  // LOADING: the same frame the loaded page uses (fixed header strip + one
  // bounded scroll region), including a tab-bar placeholder, so nothing jumps
  // when the data lands.
  if (isLoading || !data || !urn) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="disruption-detail-loading">
        <DisruptionHeader detail={undefined} isLoading isError={false} />
        <div className="density-page min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          <Skeleton className="h-9 w-64 max-w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  // App-shell pane (§18.1): fills its parent, never grows the page. The
  // descriptive header (NEX-299 item 2, mirrors the case-detail header) + the tab
  // bar stay put; only the active tab's content region scrolls. Navigation is the
  // header breadcrumb now — no back button (NEX-299 item 4).
  return (
    <div className="flex h-full min-h-0 flex-col">
      <DisruptionHeader detail={data} isLoading={false} isError={false} />
      <Tabs
        value={active}
        onValueChange={onTabChange}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {/* The tab bar is structural chrome (§18.1) — fixed, never scrolled away;
            it only scrolls sideways on a narrow phone so every tab stays reachable. */}
        <div className="flex min-w-0 shrink-0 items-center gap-3 overflow-x-auto border-b border-border bg-background px-4 pt-2 sm:px-6">
          <TabsList>
            <TabsTrigger value="overview">{t('detail.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="v2" className="gap-1.5 font-semibold text-indigo-600 dark:text-indigo-400">
              <Sparkles className="h-3.5 w-3.5" />
              {t('detail.tabs.v2')}
            </TabsTrigger>
            <TabsTrigger value="passengers">{t('detail.tabs.passengers')}</TabsTrigger>
            <TabsTrigger value="allocation">{t('detail.tabs.hotels')}</TabsTrigger>
            <TabsTrigger value="transport">{t('detail.tabs.transport')}</TabsTrigger>
            <TabsTrigger value="care">{t('detail.tabs.care')}</TabsTrigger>
            <TabsTrigger value="cost">{t('detail.tabs.cost')}</TabsTrigger>
            {canReconcile ? <TabsTrigger value="reconciliation">{t('detail.tabs.reconciliation')}</TabsTrigger> : null}
            <TabsTrigger value="activity">{t('detail.tabs.activity')}</TabsTrigger>
          </TabsList>
          {canBill && urn ? (
            <Link
              to={`/billing/disruptions/${encodeURIComponent(urn)}`}
              className="ml-auto shrink-0 whitespace-nowrap text-sm font-medium text-primary hover:underline"
              data-testid="disruption-statement-link"
            >
              {tb('action.statement')}
            </Link>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          <TabsContent value="overview" className="density-page mt-0 flex h-full flex-col overflow-hidden p-3 sm:p-4">
            <OverviewTab detail={data} disruptionUrn={urn} />
          </TabsContent>
          <TabsContent value="v2" className="density-page mt-0 flex h-full flex-col overflow-hidden p-3 sm:p-4">
            <DisruptionCommandCenterV2 disruptionUrn={urn} detail={data} />
          </TabsContent>
          <TabsContent value="passengers" className="mt-0 flex h-full flex-col overflow-hidden">
              {/* The bulk trigger lives here; a run opens the live progress dialog
                (RebookingProgressDialog) — there is no separate Progress tab. */}
            <RosterTab disruptionUrn={urn} />
          </TabsContent>
          <TabsContent value="allocation" className="mt-0 flex h-full flex-col overflow-hidden">
            <EventAllocationTab disruptionUrn={urn} detail={data} />
          </TabsContent>
          <TabsContent value="care" className="mt-0 flex h-full flex-col overflow-hidden">
            {/* LAZY like CostTab: the roster walk + care board only run while active.
                `#care?pnr=…` (roster deep link) pre-selects that row on the desk. */}
            <CareDeskTab
              disruptionUrn={urn}
              enabled={active === 'care'}
              focusPnr={active === 'care' ? hashParam(hash, 'pnr') : undefined}
            />
          </TabsContent>
          <TabsContent value="transport" className="mt-0 flex h-full flex-col overflow-hidden">
            <TransportDeskTab disruptionUrn={urn} enabled={active === 'transport'} />
          </TabsContent>
          <TabsContent value="cost" className="density-page mt-0 h-full overflow-y-auto p-4 sm:p-6">
            <CostTab disruptionUrn={urn} enabled={active === 'cost'} />
          </TabsContent>
          <TabsContent value="reconciliation" className="density-page mt-0 h-full overflow-y-auto p-4 sm:p-6">
            <ReconciliationTab disruptionUrn={urn} enabled={active === 'reconciliation'} />
          </TabsContent>
          {/* #813 — "Actividad por operador": who marked what, when (§8 manual-work metrics). */}
          <TabsContent value="activity" className="density-page mt-0 h-full overflow-y-auto p-4 sm:p-6">
            <OperatorActivityTab disruptionUrn={urn} enabled={active === 'activity'} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function OverviewTab({
  detail,
  disruptionUrn,
}: {
  detail: DisruptionDetail;
  disruptionUrn: string;
}) {
  const { t } = useTranslation('disruptions');
  // Overview is now the RESOLUTION COCKPIT (disruption-overview / NEX-376 / U1):
  // the operator resolves the WHOLE disruption here without leaving the tab. The
  // cockpit (KPI tiles + guided stepper, driven by the B1 resolution-state seam)
  // leads; the compact timeline + description + affected-flights list stay below as
  // context. The descriptive header still carries title/status/cause/airports/
  // window. Full-width (NEX-299 item 3) — no mx-auto/max-w container.
  return (
    <div className="flex h-full min-h-0 flex-col gap-2.5">
      {detail.description ? (
        <p className="shrink-0 truncate text-sm text-muted-foreground" title={detail.description}>
          {detail.description}
        </p>
      ) : null}

      {/* The resolution cockpit — facts line + the one-click hero + the guided
          stepper (rebook → tickets → hotels → vouchers → acceptance → contain). It
          owns the remaining height; its stepper list is the only scroll. */}
      <div className="min-h-0 flex-1">
        <DisruptionResolutionCockpit disruptionUrn={disruptionUrn} />
      </div>

      {/* Context, one compact line: timeline + affected flights (chips link to the case). */}
      <section
        aria-label={t('detail.flights.label')}
        className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-2 text-xs text-muted-foreground"
      >
        <span>{t('detail.timeline.detected', { when: formatDateTime(detail.detectedAt) })}</span>
        {detail.containedAt ? (
          <span>· {t('detail.timeline.contained', { when: formatDateTime(detail.containedAt) })}</span>
        ) : null}
        {detail.resolvedAt ? (
          <span>· {t('detail.timeline.resolved', { when: formatDateTime(detail.resolvedAt) })}</span>
        ) : null}
        {detail.scope.window?.fromUtc ? (
          <span>
            ·{' '}
            {t('detail.timeline.window', {
              range: `${formatDateTime(detail.scope.window.fromUtc)}${
                detail.scope.window?.toUtc
                  ? ` → ${formatDateTime(detail.scope.window.toUtc)}`
                  : ' →'
              }`,
            })}
          </span>
        ) : null}
        <span className="mx-1 text-muted-foreground/50">|</span>
        <ChildCases cases={detail.cases} />
      </section>
    </div>
  );
}

function ChildCases({ cases }: { cases: readonly DisruptionCaseRow[] }) {
  const { t } = useTranslation('disruptions');
  if (cases.length === 0) {
    return <span data-testid="disruption-cases-list">{t('detail.cases.empty')}</span>;
  }
  return (
    <ul className="flex flex-wrap items-center gap-1.5" data-testid="disruption-cases-list">
      {cases.map((c) => (
        <li key={c.caseUrn}>
          <Link
            to={`/cases/${encodeURIComponent(c.caseUrn)}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-0.5 text-xs transition-colors hover:bg-muted/50"
            title={t('detail.cases.tooltip', { status: c.status, n: c.passengerCount })}
          >
            <span className="font-semibold text-foreground">{c.flightNumber ?? c.airportCode}</span>
            {c.origin && c.destination ? (
              <span className="text-muted-foreground">
                {c.origin}→{c.destination}
              </span>
            ) : null}
            {/* Issue #662: Format in departure airport timezone */}
            {c.departureAt ? (
              <span className="text-muted-foreground">
                {formatFlightDay(c.departureAt, c.origin)}
              </span>
            ) : null}
            <span className="text-muted-foreground">
              {t('detail.cases.pax', { n: c.passengerCount })}
            </span>
            <span className="rounded bg-muted px-1 py-px text-[10px] uppercase text-muted-foreground">{c.status}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}


